import { lang } from '@umu/ukit-lang';
/**
 * @description 问卷服务
 */

import { find, getData } from '@umu/umu-util';
import {
    EOptionType,
    ESurveySectionType,
    ESectionValid,
    ISurveySectionInfo,
    IServerSectionOptionResultInfoMap,
    IServerUserResultMap,
    ISectionResult,
    ISectionOptionResultContent,
    IServerStatResultInfo,
    ISectionOptionResultInfo,
    IServerSectionOptionResultContent,
    ISectionResultMap,
    ISectionUserResult,
} from '@umu/umu-types';

/**
 * 问卷颜色总数
 * 问卷在ui上需要根据序号展示不同的颜色，9个一循环
 * 颜色列表在 businessCommon/styles/vars.scss 中有定义
 */
const SCHEME_TOTAL = 9;

/**
 * 问卷列表数据结构转换，整合单用户提交数据
 * 服务端数据只提供原始数据，端上展示用的数字字段在这里做处理。目前有
 *  sectionIndex: 问题的序号，以在list中的index为准，排除段落说明后算得的序号
 *  schemeIndex: 问题对应的颜色系列，值为1到9
 */
export function transformSectionList(list: ISurveySectionInfo[]) {
    let sectionCount = 0;
    const ret: ISurveySectionInfo[] = list.map((sectionInfo) => {
        if (
            sectionInfo.type === ESurveySectionType.PARAGRAPH ||
            (sectionInfo as ISurveySectionInfo)
                .sectionIndex /** 如果已经设置过了就跳过，因为有外部需要显式指定题目序号的情况，比如提问讨论中发言页，只展示一道题的时候 */
        ) {
            return sectionInfo;
        } else {
            return Object.assign({}, sectionInfo, {
                sectionIndex: ++sectionCount,
                schemeIndex: ((sectionCount - 1) % SCHEME_TOTAL) + 1,
            });
        }
    });
    return ret;
}

/**
 * 将单用户数据和表单数据整合，供组件展示用，主要新增加以下两个字段
 *  userValue: 用户提交的数据
 *  extraValue: 用户提交的数据-其它
 */
export function combineSectionListWithUserValue(list: ISurveySectionInfo[], userResultMap: IServerUserResultMap) {
    const ret: ISurveySectionInfo[] = list.map((sectionInfo) => {
        if (sectionInfo.type === ESurveySectionType.PARAGRAPH) {
            return sectionInfo;
        }
        return Object.assign({}, sectionInfo, getUserResult(sectionInfo, userResultMap));
    });
    return ret;
}

function getUserResult(sectionInfo: ISurveySectionInfo, userResultMap?: IServerUserResultMap) {
    const ret: ISectionResult = {
        userValue: [],
        extraValue: '',
    };
    if (!userResultMap) {
        return ret;
    }

    const sectionId = sectionInfo.id;
    const sectionResultList = userResultMap[sectionId] || [];
    sectionResultList.map((sectionResult) => {
        const answerId = String(sectionResult.answer_id); // 组件内存储的都是字符串，所以这里转换一次
        const content = sectionResult.other_content;
        if (sectionInfo.type === ESurveySectionType.NUMBER || sectionInfo.type === ESurveySectionType.TEXTAREA) {
            if (content) {
                ret.userValue.push(content);
            }
        } else {
            if (answerId) {
                ret.userValue.push(answerId);
            }
            if (content) {
                ret.extraValue = content;
            }
        }
    });
    return ret;
}

/**
 * 问卷列表数据结构转换，整合所有用户提交结果
 */
export function combineSectionListWithStatResult(list: ISurveySectionInfo[], statResult: IServerStatResultInfo) {
    const ret = list.map((sectionInfo) => {
        let resultList: Array<ISectionOptionResultContent | ISectionOptionResultInfo> = [];
        let userCount = 0;

        const sectionId = sectionInfo.id;
        const sectionStatResult = statResult[sectionId] || {
            user_count: 0,
            list: [],
        };

        const sectionType = sectionInfo.type;
        userCount = sectionStatResult.user_count;
        if (sectionType === ESurveySectionType.PARAGRAPH) {
            return sectionInfo;
        } else if (sectionType === ESurveySectionType.TEXTAREA) {
            resultList = (sectionStatResult.list as IServerSectionOptionResultContent[]).map((option) => {
                return Object.assign({}, option) as ISectionOptionResultContent;
            });
        } else if (sectionType === ESurveySectionType.NUMBER) {
            const numResultList = sectionStatResult.list as IServerSectionOptionResultInfoMap;
            const setup = sectionInfo.extend.setup;
            const min = Number((!!setup && setup.number_min) || 0);
            const max = Number((!!setup && setup.number_max) || 10);
            for (let i = min; i <= max; i++) {
                resultList.push(
                    Object.assign(
                        {
                            count: 0,
                            rate: 0,
                            title: String(i),
                        },
                        numResultList[i],
                    ) as ISectionOptionResultInfo,
                );
            }
        } else if (sectionType === ESurveySectionType.CHECKBOX || sectionType === ESurveySectionType.RADIO) {
            resultList = sectionInfo.list.map((option) => {
                const optionId = option.id;
                return Object.assign(
                    {},
                    option,
                    (sectionStatResult.list as IServerSectionOptionResultInfoMap)[optionId] || {
                        count: 0,
                        rate: 0,
                    },
                );
            });
        }

        return Object.assign({}, sectionInfo, {
            userCount,
            resultList,
        });
    });
    return ret;
}

/**
 * 验证每个题目是否正确作答
 */
export function validSection(section: ISurveySectionInfo, result: ISectionResult) {
    let invalid = ESectionValid.TRUE;
    const setup = getData(section, 'extend', 'setup');
    const isRequired = !!setup && setup.require === '1';

    const valueArr = result.userValue;
    if (section.type === ESurveySectionType.RADIO) {
        const value = valueArr[0];
        // 必选验证
        if (!value && isRequired) {
            invalid = ESectionValid.IS_EMPTY;
        }

        // 查询答案中是否有其他答案，如果有，进一步检查是否其他为必填
        if (invalid === ESectionValid.TRUE) {
            const answerArr = section.list;
            const id = value;
            // 其他答案
            for (let j = 0; j < answerArr.length; j++) {
                const option = answerArr[j];
                if (option.type === EOptionType.OTHER && option.id + '' === id) {
                    if (section.extend.extra_required === 1 && !(result.extraValue || '').trim()) {
                        invalid = ESectionValid.IS_EMPTY;
                    }
                }
            }
        }
    } else if (section.type === ESurveySectionType.CHECKBOX) {
        const min = Number((!!setup && setup.limit_options_min) || 0);
        const max = Number((!!setup && setup.limit_options_max) || 0);
        const answerItemLength = valueArr.length;
        // 答案是否满足答题要求
        const isMeetRequire = (answerItemLength >= min && answerItemLength <= max) || (min === 0 && max === 0);

        // 必选验证
        if (isRequired && answerItemLength === 0) {
            invalid = ESectionValid.IS_EMPTY;
        }

        // 必选验证,是否符合答题要求
        if (isRequired && answerItemLength !== 0 && !isMeetRequire) {
            invalid = ESectionValid.NOT_MEET_REQUIREMENT;
        }

        // 非必选,但是要求符合答题要求
        if (!isRequired && answerItemLength !== 0 && !isMeetRequire) {
            invalid = ESectionValid.NOT_MEET_REQUIREMENT;
        }

        if (invalid === ESectionValid.TRUE) {
            const answerArr = section.list;
            for (let i = 0; i < valueArr.length; i++) {
                const id = valueArr[i];
                // 其他答案
                for (let j = 0; j < answerArr.length; j++) {
                    const option = answerArr[j];
                    if (option.type === EOptionType.OTHER && option.id + '' === id) {
                        if (section.extend.extra_required === 1 && !(result.extraValue || '').trim()) {
                            invalid = ESectionValid.IS_EMPTY;
                        }
                    }
                }
            }
        }
    } else if (section.type === ESurveySectionType.TEXTAREA) {
        const value = valueArr[0];
        // 必选验证
        if (
            (!value || !value.trim()) &&
            // && !(result.pic_url && $.isArray(result.pic_url) && result.pic_url.length > 0)
            isRequired
        ) {
            invalid = ESectionValid.IS_EMPTY;
        }
    } else if (section.type === ESurveySectionType.NUMBER) {
        const value = valueArr[0];
        // 必选验证
        if (!value && isRequired) {
            invalid = ESectionValid.IS_EMPTY;
        }
        // } else if (this.isInput()) {
        //     // 必选验证
        //     if ((!result || !result.text) && isRequired) {
        //         invalid = { type: 'input', errMsgType: 'required', errType: '1' };
        //     }
    }

    return invalid;
}

/**
 * 对 list 中间数据进行验证，返回错误提示。如果无错误则返回空
 */
export function getErrorTip(sectionResultMap: ISectionResultMap) {
    let ret = '';

    const invalid: number[] = [];
    let type;

    for (const id in sectionResultMap) {
        if (sectionResultMap.hasOwnProperty(id)) {
            const data = sectionResultMap[id];
            const isValid = data.isValid;

            if (isValid === ESectionValid.IS_EMPTY) {
                invalid.push(data.sectionIndex || 1);
            } else if (isValid === ESectionValid.NOT_MEET_REQUIREMENT) {
                type = isValid;
            }
        }
    }

    if (invalid.length || type === ESectionValid.NOT_MEET_REQUIREMENT) {
        if (invalid.length === 0 && type === ESectionValid.NOT_MEET_REQUIREMENT) {
            ret = lang('请按题目要求回答问题');
        } else {
            ret = 'Q' + invalid.join(', ') + lang('还未回答');
        }
    }

    return ret;
}

/**
 * 获取提交结果数据
 */
export function getSubmitResult(sectionResultMap: ISectionResultMap, submitEmptyResult?: boolean) {
    const ret: ISectionUserResult[] = [];
    for (const id in sectionResultMap) {
        if (sectionResultMap.hasOwnProperty(id)) {
            const result = sectionResultMap[id];
            const questionId = Number(id);
            result.userValue.map((value) => {
                value = value.trim();
                if (
                    result.sectionType === ESurveySectionType.NUMBER ||
                    result.sectionType === ESurveySectionType.TEXTAREA
                ) {
                    // trim后如果没有内容则判空
                    (submitEmptyResult || value) &&
                        ret.push({
                            question_id: questionId,
                            other_content: value,
                            type: result.sectionType,
                        });
                } else {
                    // 单多选的‘其它’选项特殊识别
                    ret.push(
                        Object.assign(
                            {
                                question_id: questionId,
                                answer_id: value,
                                type: result.sectionType,
                            },
                            value === String(result.extraId)
                                ? {
                                    other_content: (result.extraValue || '').trim(),
                                }
                                : {},
                        ),
                    );
                }
            });
        }
    }
    return ret;
}

/**
 * 初始化 list 中间结果的map
 */
export function getResultListState(sectionList: ISurveySectionInfo[]) {
    const ret: ISectionResultMap = {};
    sectionList.map((sectionInfo) => {
        if (sectionInfo.type === ESurveySectionType.PARAGRAPH) {
            return;
        }
        const sectionId = sectionInfo.id;
        const sectionResult = {
            userValue: sectionInfo.userValue || [],
            extraValue: sectionInfo.extraValue,
            extraId: getExtraOptionId(sectionInfo),
            sectionType: sectionInfo.type,
        };
        ret[sectionId] = buildSectionResult(sectionInfo, sectionResult, validSection(sectionInfo, sectionResult));
    });
    return ret;
}

/**
 * 构建中间结果
 */
// tslint:disable-next-line:max-line-length
export function buildSectionResult(
    sectionInfo: ISurveySectionInfo,
    sectionResult?: ISectionResult,
    isValid?: ESectionValid,
) {
    return Object.assign(
        {
            sectionId: sectionInfo.id,
            sectionType: sectionInfo.type,
            sectionIndex: sectionInfo.sectionIndex,
            isValid,
        },
        sectionResult,
    );
}

export function getExtraOptionId(sectionInfo: ISurveySectionInfo) {
    const extraOption = sectionInfo.list.filter((option) => {
        return option.type === EOptionType.OTHER;
    })[0];
    if (!extraOption) {
        return 0;
    }
    return extraOption.id;
}

/**
 *
 *  发言页只展示一道题目，但题号需要是完整列表中的那个序号，
 *  这里先根据原始数据设置题目序号，再从整体数据中选出需要展示的那一道
 */
export function getSectionListById(questionList: ISurveySectionInfo[], questionId: number) {
    let list: ISurveySectionInfo[] = JSON.parse(JSON.stringify(questionList));
    list = transformSectionList(list);
    const question = find(list, (item: ISurveySectionInfo) => {
        return +item.id === +questionId;
    });
    if (question) {
        // 发言页面中颜色不随序号变化
        question.schemeIndex = 1;
    }
    list = question ? [question] : [];
    return list;
}
